<template>
  <div :class="{ [$style.container]: true, [$style['container--fixed']]: !responsive }">
    <v-badge
      v-if="availabilityStatus && availabilityText"
      bordered
      :class="badgeClasses"
      :color="color"
      overlap
      :value="showFutureAvailability"
    >
      <template #badge>
        <v-tooltip
          v-if="hasFutureAvailabilities"
          v-model="showTooltip"
          left
          :close-delay="150"
          :content-class="$style.availibilityTooltip"
        >
          <template #activator="{ on, attrs }">
            <v-icon v-bind="attrs" color="black" size="24px" v-on="on">mdi-clock-outline</v-icon>
          </template>
          <h4 v-if="hasFutureAvailabilities" class="mb-1 h5">
            {{ $t('product.availability.plannedListHeadline') }}
          </h4>
          <ul class="unstyled-list">
            <li v-for="item in futureAvailabilities" :key="item">
              {{ item }}
            </li>
          </ul>
        </v-tooltip>
        <v-tooltip
          v-else
          v-model="showTooltip"
          left
          :close-delay="150"
          :content-class="$style.availibilityTooltip"
        >
          <template #activator="{ on, attrs }">
            <v-icon v-bind="attrs" color="black" size="24px" v-on="on">mdi-clock-outline</v-icon>
          </template>
          <p class="mb-0">
            {{ $t('product.availability.no_further_productions') }}
          </p>
        </v-tooltip>
      </template>
      <span>{{ availabilityText }}</span>
      <v-tooltip
        v-if="showFirstAvailabilityChange"
        v-model="availabilityChangeTooltip"
        :color="firstAvailabilityIsAvailable ? 'success' : 'error'"
        right
        :content-class="$style.availibilityTooltip"
      >
        <template #activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on"> </span>
        </template>
        <p class="mb-0" :class="{ ['white--text']: !firstAvailabilityIsAvailable }">
          {{
            firstAvailabilityIsAvailable
              ? $t('product.toolTip.availabilityUpdated')
              : $t('product.toolTip.noLongerAvailable')
          }}
        </p>
      </v-tooltip>
    </v-badge>
    <v-skeleton-loader v-else :class="$style.loader" type="chip" />
  </div>
</template>

<script>
import { get, snakeCase, values, isUndefined, head, tail, min } from 'lodash-es'
// import { COMPANY_CODE_TO_OVERSELLING } from '@/js/helper/dp-usermanagement-constants.js'
import {
  AVAILABILITY_CONSTANTS,
  AVAILABILITY_TYPES,
  AVAILABILITY_LIMITS,
} from '@/js/plugins/store/modules/products/constants.js'
import { CLIENT_TO_COMPANY_CODE } from '@/js/helper/dp-usermanagement-constants.js'
import { isProductAvailable } from '@/js/plugins/store/modules/products/helper.js'

export const AVAILABILITY_BADGE_CONSTANTS = {
  EVENTS: {
    AVAILABILITY_STATUS_SET: 'availabilityStatusSet',
  },
  AVAILABLE: 'available',
}

export default {
  name: 'AvailabilityBadge',
  props: {
    availability: {
      type: Array,
      default: () => [],
    },
    customAvailabilityStatus: {
      type: String,
      validator(value) {
        return values(AVAILABILITY_CONSTANTS.TYPES).indexOf(value) >= 0
      },
      default: undefined,
    },
    showFutureAvailability: {
      type: Boolean,
      default: true,
    },
    responsive: {
      type: Boolean,
      default: false,
    },
    articleType: {
      type: String,
      default: 'bike',
    },
    archiveItem: {
      type: Boolean,
      default: false,
    },
    hasSoldOutSelling: {
      type: Boolean,
      default: false,
    },
    groupPrices: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showTooltip: false,
      availabilityChangeTooltip: false,
      showFirstAvailabilityChange: false,
    }
  },
  computed: {
    firstAvailability() {
      return head(this.availability)
    },
    firstAvailabilityIsAvailable() {
      return isProductAvailable(this.firstAvailability)
    },
    firstAvailabilityHasChange() {
      return !!this.firstAvailability?.change
    },
    hasFutureAvailabilities() {
      return this.availability.length > 1
    },
    futureAvailabilities() {
      return tail(this.availability).map(item => {
        return this.$t(`product.availability.plannedListEntry`, {
          stock: this.calculateStocklevel(item['stock-level']),
          week: item.week,
          year: `${item.year}`.slice(2),
        })
      })
    },
    color() {
      return this.hasFutureAvailabilities ? 'primary' : 'grey'
    },
    availabilityStatus() {
      const status =
        this.customAvailabilityStatus || get(this.firstAvailability, AVAILABILITY_CONSTANTS.STATUS)
      return status === AVAILABILITY_TYPES.SOLD_OUT && this.hasSoldOutSelling
        ? AVAILABILITY_TYPES.SOLD_OUT_SELLING
        : status
    },
    availabilityWeek() {
      return get(this.firstAvailability, AVAILABILITY_CONSTANTS.WEEK)
    },
    availabilityYear() {
      return get(this.firstAvailability, AVAILABILITY_CONSTANTS.YEAR)
    },
    availabilityText() {
      const stockLevel = this.calculateStocklevel(
        this.hasStockLevel ? this.availabilityStockLevel : 0,
      )
      const translationParams = {
        stock: !isUndefined(this.currentStockLevel)
          ? this.$t('product.availability.currentOfTotal', {
              current: this.currentStockLevel,
              stock: stockLevel,
            })
          : stockLevel,
        week: this.availabilityWeek,
        year: `${this.availabilityYear}`.slice(2),
      }
      const textWithArticleTypeKey = `product.availability.${this.articleType}.${snakeCase(
        this.availabilityStatus,
      )}`
      const textWithArticleTypeTrans = this.$t(textWithArticleTypeKey, translationParams)
      return textWithArticleTypeKey === textWithArticleTypeTrans
        ? this.$t(`product.availability.${snakeCase(this.availabilityStatus)}`, translationParams)
        : textWithArticleTypeTrans
    },
    availabilityStockLevel() {
      return get(this.firstAvailability, AVAILABILITY_CONSTANTS.STOCKLEVEL)
    },
    currentStockLevel() {
      return get(this.firstAvailability, AVAILABILITY_CONSTANTS.CURRENT_STOCKLEVEL)
    },
    hasStockLevel() {
      return typeof this.availabilityStockLevel !== 'undefined' && this.availabilityStockLevel >= 0
    },
    badgeClasses() {
      return [
        this.$style.badge,
        this.$style[`badge--${this.availabilityStatus}`],
        {
          [this.$style['badge--text-black']]:
            this.availabilityStatus === AVAILABILITY_CONSTANTS.TYPES.AVAILABLE_FROM ||
            this.availabilityStatus === AVAILABILITY_CONSTANTS.TYPES.AVAILABLE_SOON ||
            this.availabilityStatus === AVAILABILITY_CONSTANTS.TYPES.AVAILABLE_LATER ||
            this.availabilityStatus === AVAILABILITY_CONSTANTS.TYPES.AVAILABLE ||
            this.availabilityStatus === AVAILABILITY_CONSTANTS.TYPES.MAYBE_AVAILABLE,
        },
      ]
    },
    companyCodes() {
      return Object.keys(this.groupPrices).map(client => CLIENT_TO_COMPANY_CODE?.[client])
    },
    stockLimit() {
      return min(
        this.companyCodes
          .map(
            companyCode =>
              AVAILABILITY_LIMITS?.[companyCode]?.[this.articleType] ||
              AVAILABILITY_LIMITS['default'][this.articleType] ||
              0,
          )
          .filter(limit => limit),
      )
    },
  },
  watch: {
    firstAvailabilityHasChange() {
      if (this.hasSoldOutSelling || !this.firstAvailabilityHasChange) {
        return
      }
      this.showFirstAvailabilityChange = true
      this.availabilityChangeTooltip = true
      setTimeout(() => {
        this.availabilityChangeTooltip = false
        this.showFirstAvailabilityChange = false
      }, 2000)
    },
  },
  methods: {
    calculateStocklevel(stock) {
      return this.stockLimit && stock > this.stockLimit ? `${this.stockLimit}+` : `${stock}`
    },
  },
}
</script>

<style lang="scss" module>
.container {
  // @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  //   width: 11.275rem;
  // }
  display: flex;
  justify-content: center;
  width: auto;
  max-width: 11.275rem;
}

.container--fixed {
  @media (min-width: map-get($grid-breakpoints, 'md')) {
    width: 11.275rem;
  }
}

.loader > div,
.badge {
  border-radius: 1rem;
}

.loader > div {
  height: 1.25rem;
}

.badge {
  @media print {
    font-size: var(--font-xs);
  }

  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: 1.25rem;

  padding: 0.25rem 0.75rem;

  color: var(--white);
  font-weight: var(--font-weight-bold);

  font-size: var(--font-sm);
  line-height: 1;
  text-align: center;

  background: var(--grey);

  &--text-black {
    color: var(--black);
  }

  &--available,
  &--maybe-available {
    @media print {
      border: 1px solid var(--green);
    }

    background: var(--green);
  }

  &--available-from,
  &--available-soon,
  &--available-later {
    @media print {
      border: 1px solid var(--yellow);
    }
    background: var(--yellow);
  }

  &--few-available {
    @keyframes FewAvailable {
      0% {
        background-position: 75% 0%;
      }

      50% {
        background-position: 26% 100%;
      }

      100% {
        background-position: 75% 0%;
      }
    }
    @media print {
      border: 1px solid var(--warning-darken-1);
    }

    background: linear-gradient(90deg, var(--green), var(--red));
    background-size: 200% 200%;

    animation: FewAvailable 4s ease infinite;
  }

  &--preorder {
    @media print {
      border: 1px solid var(--blue-darken-1);
    }
    background: var(--blue-darken-1);
  }

  &--sold-out {
    @media print {
      border: 1px solid var(--red-lighten-2);
    }
    background: var(--red-lighten-2);
  }

  :global(.v-badge__badge) {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0;

    color: var(--black);

    border-radius: 16px;

    i {
      z-index: 2;
    }
  }
}

.availibilityTooltip {
  color: var(--text) !important;

  background-color: var(--primary) !important;
  opacity: 0.975 !important;
}
</style>
